/**
 * Pelota, Petardos e Faísca (item 4.2): posse, passe, arremesso com mira, gol pelo aro, rebatida do batedor,
 * atordoamento, defesa do goleiro e captura da Faísca. Determinístico: o acaso sai de `state.rng`.
 */
import { ABILITIES, BALL, FLIGHT, MATCH, PITCH, byStat, hoopCenters, type Side } from "@besenliga/shared";
import { abilityActive } from "./abilities.js";
import type { CollisionEvent } from "./collision.js";
import type { AthleteState, Team, Vec3 } from "./flight.js";
import { BUTTON, type InputFrame } from "./input.js";
import { nextFloat, type RngState } from "./rng.js";

const TICK = 1 / 30;
const ticks = (seconds: number) => Math.round(seconds * 30);

export interface PelotaState {
  position: Vec3;
  velocity: Vec3;
  /** Quem carrega a bola; null = bola solta. */
  holder: string | null;
  /** Último a tocar (autor do gol, do passe ou do arremesso). */
  lastTouch: string | null;
  lastTeam: Team | null;
  /** Ticks em que `lastTouch` ainda não pode pegar a bola de volta. */
  regrabTicks: number;
}

export type PetardoMode = "descanso" | "caca" | "lancado";

export interface PetardoState {
  position: Vec3;
  velocity: Vec3;
  mode: PetardoMode;
  modeTicks: number;
  /** Batedor que lançou (não é atingido pelo próprio Petardo). */
  hitBy: string | null;
  /** Atleta ignorado por um tempo depois de ser atingido. */
  ignore: string | null;
  ignoreTicks: number;
}

export interface FaiscaState {
  /** Já apareceu e ainda não foi capturada. */
  active: boolean;
  captured: boolean;
  capturedBy: string | null;
  position: Vec3;
  velocity: Vec3;
  waypoint: Vec3;
  wanderTicks: number;
  /** Ticks desde que apareceu (para ela ficar mais lenta a cada minuto). */
  activeTicks: number;
  /** Tick em que aparece. */
  appearTick: number;
}

export interface BallsState {
  pelota: PelotaState;
  petardos: PetardoState[];
  faisca: FaiscaState;
}

export type PlayKind =
  | "posse"
  | "passe"
  | "arremesso"
  | "gol"
  | "solta"
  | "roubo"
  | "defesa"
  | "rebatida"
  | "atordoado"
  | "aparece"
  | "captura"
  // Regras da partida (item 4.3).
  | "inicio"
  | "falta"
  | "penalti"
  | "reinicio"
  | "morte-subita"
  | "fim"
  // Habilidades (item 4.4).
  | "habilidade"
  | "queda";

export interface PlayEvent {
  kind: PlayKind;
  /** Quem fez a jogada (ou quem sofreu, em "atordoado"). */
  athlete: string;
  /** Alvo do passe ou da rebatida, ou quem rebateu o Petardo que atordoou. */
  other?: string;
  /** Time que marcou (gol, captura). */
  team?: Team;
  /** Índice do aro (0 a 2) no gol. */
  hoop?: number;
  /** Tipo da falta, em "falta". */
  foul?: FoulKind;
}

export type FoulKind = "empurrao" | "bloqueio" | "petardo-goleiro";

const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const len = (a: Vec3) => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
const dist = (a: Vec3, b: Vec3) => len(sub(a, b));

/** Lado defendido por cada time: a casa defende os aros em x < 0. */
export const DEFENDS: Record<Team, Side> = { casa: -1, fora: 1 };
const opponent = (t: Team): Team => (t === "casa" ? "fora" : "casa");

export function createBalls(appearTick: number): BallsState {
  const restTicks = ticks(BALL.petardo.kickoffRestSeconds);
  return {
    pelota: {
      position: v3(0, 10, 0),
      velocity: v3(),
      holder: null,
      lastTouch: null,
      lastTeam: null,
      regrabTicks: 0,
    },
    petardos: [-1, 1].map((s) => ({
      position: v3(0, 14, s * 10),
      velocity: v3(),
      mode: "descanso" as const,
      modeTicks: restTicks,
      hitBy: null,
      ignore: null,
      ignoreTicks: 0,
    })),
    faisca: {
      active: false,
      captured: false,
      capturedBy: null,
      position: v3(0, 25, 0),
      velocity: v3(),
      waypoint: v3(0, 25, 0),
      wanderTicks: 0,
      activeTicks: 0,
      appearTick,
    },
  };
}

/** Dentro da área do próprio goleiro (meia-elipse à frente dos aros do seu lado). */
export function inKeeperArea(a: AthleteState): boolean {
  const side = DEFENDS[a.team];
  const cx = side * (PITCH.hoopX - 1);
  if (side * (a.position.x - cx) > 0) return false;
  const ux = (a.position.x - cx) / PITCH.keeperAreaX;
  const uz = a.position.z / PITCH.keeperAreaZ;
  return ux * ux + uz * uz <= 1;
}

function canHold(a: AthleteState): boolean {
  if (a.stunTicks > 0) return false;
  if (a.role === "artilheiro") return true;
  return a.role === "goleiro" && inKeeperArea(a);
}

/** Valor sorteado em [-1, 1). */
function signed(rng: RngState): [RngState, number] {
  const [r, f] = nextFloat(rng);
  return [r, f * 2 - 1];
}

/**
 * Velocidade para a bola ir de `from` até `to` com velocidade horizontal `speed`, compensando a gravidade
 * (e um pouco do arrasto do ar).
 */
function ballistic(from: Vec3, to: Vec3, speed: number): Vec3 {
  const d = sub(to, from);
  const horiz = Math.sqrt(d.x * d.x + d.z * d.z);
  const t = Math.max(0.25, horiz / speed);
  const k = 1 + (BALL.pelota.dragPerSecond * t) / 2;
  return {
    x: (d.x / t) * k,
    y: (d.y / t + 0.5 * BALL.pelota.gravity * t) * k,
    z: (d.z / t) * k,
  };
}

/** Choque de uma esfera com chão, teto, mureta, postes e aros. Altera posição e velocidade. */
function bounceWorld(p: Vec3, v: Vec3, r: number, bounce: number, floor: number): boolean {
  let hit = false;
  const reflect = (n: Vec3, depth: number) => {
    p.x += n.x * depth;
    p.y += n.y * depth;
    p.z += n.z * depth;
    const vn = v.x * n.x + v.y * n.y + v.z * n.z;
    if (vn < 0) {
      const k = (1 + bounce) * vn;
      v.x -= k * n.x;
      v.y -= k * n.y;
      v.z -= k * n.z;
    }
    hit = true;
  };
  if (p.y < floor + r) reflect(v3(0, 1, 0), floor + r - p.y);
  const ceil = PITCH.ceiling + 8;
  if (p.y > ceil) reflect(v3(0, -1, 0), p.y - ceil);
  const wx = PITCH.sandRadiusX - r;
  const wz = PITCH.sandRadiusZ - r;
  const ux = p.x / wx;
  const uz = p.z / wz;
  const e = ux * ux + uz * uz;
  if (e > 1) {
    const s = 1 / Math.sqrt(e);
    p.x *= s;
    p.z *= s;
    const gx = p.x / (wx * wx);
    const gz = p.z / (wz * wz);
    const gl = Math.sqrt(gx * gx + gz * gz);
    reflect(v3(-gx / gl, 0, -gz / gl), 0);
  }
  for (const side of [-1, 1] as const)
    for (const h of hoopCenters(side)) {
      const poleTop = h.y - PITCH.hoopRadius;
      if (p.y < poleTop + r) {
        const px = p.x - h.x;
        const pz = p.z - h.z;
        const d = Math.sqrt(px * px + pz * pz);
        const min = FLIGHT.hoopPoleRadius + r;
        if (d < min && d > 1e-6) reflect(v3(px / d, 0, pz / d), min - d);
      }
      const oy = p.y - h.y;
      const oz = p.z - h.z;
      const radial = Math.sqrt(oy * oy + oz * oz);
      if (radial > 1e-6) {
        const q = v3(p.x - h.x, oy - (oy / radial) * PITCH.hoopRadius, oz - (oz / radial) * PITCH.hoopRadius);
        const d = len(q);
        const min = FLIGHT.hoopTubeRadius + r;
        if (d < min && d > 1e-6) reflect(v3(q.x / d, q.y / d, q.z / d), min - d);
      }
    }
  return hit;
}

/** A Pelota atravessou o plano de um aro, por dentro do furo, vindo do campo: devolve o lado e o aro. */
function goalCrossing(prev: Vec3, cur: Vec3): { side: Side; hoop: number } | null {
  for (const side of [-1, 1] as const) {
    const X = side * PITCH.hoopX;
    if (side * (cur.x - prev.x) <= 0) continue;
    if (side * (prev.x - X) > 0 || side * (cur.x - X) < 0) continue;
    const t = (X - prev.x) / (cur.x - prev.x);
    const y = prev.y + (cur.y - prev.y) * t;
    const z = prev.z + (cur.z - prev.z) * t;
    const inner = PITCH.hoopRadius - FLIGHT.hoopTubeRadius - BALL.pelota.radius;
    const hoops = hoopCenters(side);
    for (let i = 0; i < hoops.length; i++) {
      const h = hoops[i];
      if (!h) continue;
      const dy = y - h.y;
      const dz = z - h.z;
      if (dy * dy + dz * dz < inner * inner) return { side, hoop: i };
    }
  }
  return null;
}

/** Atordoa: perde o controle por MATCH.stunSeconds e cai MATCH.stunDropMeters. */
function stun(a: AthleteState): void {
  a.stunTicks = ticks(MATCH.stunSeconds);
  // Velocidade de queda que, freando com a aceleração vertical do voo, cai exatamente a altura da regra.
  a.velocity.y = -Math.sqrt(2 * FLIGHT.climbAcceleration * MATCH.stunDropMeters);
  a.velocity.x *= 0.3;
  a.velocity.z *= 0.3;
}

export interface BallStepResult {
  balls: BallsState;
  rng: RngState;
  plays: PlayEvent[];
}

/**
 * Avança as bolas um tick, depois do voo e das colisões dos atletas. Altera `athletes` (atordoamento,
 * botões do tick anterior e carga) e devolve as bolas novas.
 */
export function stepBalls(
  prev: BallsState,
  athletes: AthleteState[],
  inputs: InputFrame,
  collisions: readonly CollisionEvent[],
  tick: number,
  rngIn: RngState,
  /** Quem usou a habilidade neste tick (para a Rebatida Dupla). */
  abilities: ReadonlySet<string> = new Set(),
): BallStepResult {
  let rng = rngIn;
  const plays: PlayEvent[] = [];
  const byId = new Map(athletes.map((a) => [a.id, a]));
  const pressed = new Map<string, number>();
  const released = new Map<string, number>();
  for (const a of athletes) {
    const now = a.stunTicks > 0 ? 0 : (inputs[a.id]?.buttons ?? 0);
    pressed.set(a.id, now & ~a.buttons);
    released.set(a.id, a.buttons & ~now);
  }
  const did = (a: AthleteState, button: number) => ((pressed.get(a.id) ?? 0) & button) !== 0;

  // ---------------------------------------------------------------- Pelota
  const pel: PelotaState = {
    ...prev.pelota,
    position: { ...prev.pelota.position },
    velocity: { ...prev.pelota.velocity },
    regrabTicks: Math.max(0, prev.pelota.regrabTicks - 1),
  };
  const release = (by: AthleteState, velocity: Vec3, kind: PlayKind, other?: string) => {
    pel.holder = null;
    pel.lastTouch = by.id;
    pel.lastTeam = by.team;
    pel.regrabTicks = ticks(BALL.pelota.regrabSeconds);
    pel.velocity = velocity;
    plays.push(other === undefined ? { kind, athlete: by.id } : { kind, athlete: by.id, other });
  };

  const holder = pel.holder ? byId.get(pel.holder) : undefined;
  if (holder) {
    const hp = holder.position;
    const knock = collisions.find(
      (c) =>
        c.kind === "atleta" &&
        c.impactSpeed > BALL.pelota.knockLooseImpact &&
        (c.athlete === holder.id || c.other === holder.id) &&
        !abilityActive(holder, "finta") &&
        byId.get(c.athlete === holder.id ? (c.other ?? "") : c.athlete)?.team !== holder.team,
    );
    if (holder.stunTicks > 0 || (holder.role === "goleiro" && !inKeeperArea(holder))) {
      release(holder, { x: holder.velocity.x, y: holder.velocity.y + 3, z: holder.velocity.z }, "solta");
    } else if (knock) {
      const thief = knock.athlete === holder.id ? knock.other : knock.athlete;
      release(holder, { x: holder.velocity.x * 0.5, y: 4, z: holder.velocity.z * 0.5 }, "roubo", thief);
    } else if (did(holder, BUTTON.action)) {
      // Passe: entre os companheiros que podem receber, o mais alinhado com a vassoura; senão, o mais perto.
      const mates = athletes.filter(
        (m) =>
          m.team === holder.team &&
          m.id !== holder.id &&
          canHold(m) &&
          dist(m.position, hp) < BALL.pelota.passRange,
      );
      // Quem está pedindo a bola tem preferência (o mais perto deles); senão, o mais alinhado com a vassoura.
      const callers = mates
        .filter((m) => (inputs[m.id]?.buttons ?? 0) & BUTTON.call && m.stunTicks === 0)
        .sort((a, b) => dist(a.position, hp) - dist(b.position, hp));
      let best: AthleteState | undefined = callers[0];
      let bestDot: number = BALL.pelota.aimCone;
      for (const m of best ? [] : mates) {
        const d = sub(m.position, hp);
        const hl = Math.sqrt(d.x * d.x + d.z * d.z);
        const dot = hl > 0 ? (d.x * holder.heading.x + d.z * holder.heading.z) / hl : 0;
        if (dot > bestDot) {
          bestDot = dot;
          best = m;
        }
      }
      if (!best) best = [...mates].sort((a, b) => dist(a.position, hp) - dist(b.position, hp))[0];
      const speed = byStat(BALL.pelota.passSpeed, holder.stats.passe);
      if (best) {
        const flight = dist(best.position, hp) / speed;
        const aim = {
          x: best.position.x + best.velocity.x * flight,
          y: best.position.y + best.velocity.y * flight + BALL.pelota.holdUp,
          z: best.position.z + best.velocity.z * flight,
        };
        const err = byStat(BALL.pelota.passError, holder.stats.passe) * dist(aim, hp);
        let ex: number;
        let ey: number;
        let ez: number;
        [rng, ex] = signed(rng);
        [rng, ey] = signed(rng);
        [rng, ez] = signed(rng);
        const target = { x: aim.x + ex * err, y: aim.y + ey * err * 0.5, z: aim.z + ez * err };
        release(holder, ballistic(pel.position, target, speed), "passe", best.id);
      } else {
        release(holder, { x: holder.heading.x * speed, y: 2, z: holder.heading.z * speed }, "passe");
      }
    } else if ((released.get(holder.id) ?? 0) & BUTTON.charge) {
      // Arremesso carregado: mira no aro atacado mais alinhado com a vassoura.
      const charge = Math.min(1, holder.chargeTicks / ticks(BALL.pelota.chargeSeconds));
      const range = BALL.pelota.throwSpeed;
      const top = byStat({ min: range.min, max: range.max }, holder.stats.arremesso);
      const speed = range.min + (top - range.min) * charge;
      const side = -DEFENDS[holder.team] as Side;
      let aim: Vec3 | null = null;
      let bestDot: number = BALL.pelota.aimCone;
      for (const h of hoopCenters(side)) {
        const d = sub(h, hp);
        const hl = Math.sqrt(d.x * d.x + d.z * d.z);
        const dot = hl > 0 ? (d.x * holder.heading.x + d.z * holder.heading.z) / hl : 0;
        if (dot > bestDot && len(d) < BALL.pelota.throwRange) {
          bestDot = dot;
          aim = { ...h };
        }
      }
      if (aim) {
        const err =
          byStat(BALL.pelota.throwError, holder.stats.arremesso) * dist(aim, hp) * (1 - 0.5 * charge);
        let ey: number;
        let ez: number;
        [rng, ey] = signed(rng);
        [rng, ez] = signed(rng);
        release(
          holder,
          ballistic(pel.position, { x: aim.x, y: aim.y + ey * err, z: aim.z + ez * err }, speed),
          "arremesso",
        );
      } else {
        release(
          holder,
          { x: holder.heading.x * speed, y: speed * 0.15, z: holder.heading.z * speed },
          "arremesso",
        );
      }
    } else {
      pel.position = {
        x: hp.x + holder.heading.x * BALL.pelota.holdForward,
        y: hp.y + BALL.pelota.holdUp,
        z: hp.z + holder.heading.z * BALL.pelota.holdForward,
      };
      pel.velocity = { ...holder.velocity };
    }
  } else if (pel.holder) {
    pel.holder = null;
  }

  if (!pel.holder) {
    const before = { ...pel.position };
    const g = BALL.pelota.gravity * TICK;
    const keep = 1 - BALL.pelota.dragPerSecond * TICK;
    pel.velocity = { x: pel.velocity.x * keep, y: (pel.velocity.y - g) * keep, z: pel.velocity.z * keep };
    pel.position = {
      x: pel.position.x + pel.velocity.x * TICK,
      y: pel.position.y + pel.velocity.y * TICK,
      z: pel.position.z + pel.velocity.z * TICK,
    };
    const goal = goalCrossing(before, pel.position);
    if (goal && pel.lastTeam) {
      const scorer = opponent(goal.side === DEFENDS.casa ? "casa" : "fora");
      plays.push({ kind: "gol", athlete: pel.lastTouch ?? "", team: scorer, hoop: goal.hoop });
    }
    const onGround = pel.position.y <= BALL.pelota.radius + 0.01;
    bounceWorld(pel.position, pel.velocity, BALL.pelota.radius, BALL.pelota.bounce, 0);
    if (onGround) {
      const f = 1 - BALL.pelota.groundFriction * TICK;
      pel.velocity.x *= f;
      pel.velocity.z *= f;
    }

    // Defesa do goleiro fora das mãos: soco que manda a bola de volta para o campo.
    for (const a of athletes) {
      if (a.role !== "goleiro" || !did(a, BUTTON.action) || canHold(a)) continue;
      const wall = abilityActive(a, "muralha") ? ABILITIES.muralha.reachFactor : 1;
      if (dist(a.position, pel.position) > BALL.keeperReach * wall) continue;
      const out = -DEFENDS[a.team];
      pel.velocity = { x: out * BALL.keeperPunchSpeed, y: 6, z: pel.position.z > a.position.z ? 4 : -4 };
      pel.lastTouch = a.id;
      pel.lastTeam = a.team;
      plays.push({ kind: "defesa", athlete: a.id });
      break;
    }

    // Quem pode pegar: o mais perto dentro do alcance (empate pelo id).
    let taker: AthleteState | undefined;
    let takerD = Infinity;
    for (const a of athletes) {
      if (!canHold(a)) continue;
      if (a.id === pel.lastTouch && pel.regrabTicks > 0) continue;
      const wall = abilityActive(a, "muralha") ? ABILITIES.muralha.reachFactor : 1;
      const reach =
        FLIGHT.athleteRadius + BALL.pelota.radius + byStat(BALL.pelota.catchReach, a.stats.reflexo) * wall;
      const d = dist(a.position, pel.position);
      if (d <= reach && d < takerD) {
        taker = a;
        takerD = d;
      }
    }
    if (taker) {
      pel.holder = taker.id;
      pel.lastTouch = taker.id;
      pel.lastTeam = taker.team;
      pel.regrabTicks = 0;
      plays.push({ kind: "posse", athlete: taker.id });
    } else {
      // Quem não pode carregar a Pelota rebate nela.
      for (const a of athletes) {
        if (canHold(a)) continue;
        const d = sub(pel.position, a.position);
        const dl = len(d);
        const min = FLIGHT.athleteRadius + BALL.pelota.radius;
        if (dl >= min || dl < 1e-6) continue;
        const n = v3(d.x / dl, d.y / dl, d.z / dl);
        pel.position = {
          x: a.position.x + n.x * min,
          y: a.position.y + n.y * min,
          z: a.position.z + n.z * min,
        };
        const rv =
          (pel.velocity.x - a.velocity.x) * n.x +
          (pel.velocity.y - a.velocity.y) * n.y +
          (pel.velocity.z - a.velocity.z) * n.z;
        if (rv < 0) {
          const k = (1 + BALL.pelota.bounce) * rv;
          pel.velocity = {
            x: pel.velocity.x - k * n.x,
            y: pel.velocity.y - k * n.y,
            z: pel.velocity.z - k * n.z,
          };
        }
      }
    }
  }

  // ---------------------------------------------------------------- Petardos
  const petardos = prev.petardos.map((pp) => {
    const p: PetardoState = {
      ...pp,
      position: { ...pp.position },
      velocity: { ...pp.velocity },
      modeTicks: Math.max(0, pp.modeTicks - 1),
      ignoreTicks: Math.max(0, pp.ignoreTicks - 1),
    };
    if (p.ignoreTicks === 0) p.ignore = null;
    if (p.mode !== "caca" && p.modeTicks === 0) p.mode = "caca";
    return p;
  });

  // Rebate `target` no adversário mais alinhado com a vassoura (menos `skip`); devolve o alvo escolhido.
  const bat = (a: AthleteState, target: PetardoState, skip?: string): AthleteState | undefined => {
    let aim: AthleteState | undefined;
    let bestDot: number = BALL.pelota.aimCone;
    for (const o of athletes) {
      if (o.team === a.team || o.stunTicks > 0 || o.id === skip) continue;
      const d = sub(o.position, a.position);
      const l = len(d);
      const dot = l > 0 ? (d.x * a.heading.x + d.z * a.heading.z) / l : 0;
      if (dot > bestDot) {
        bestDot = dot;
        aim = o;
      }
    }
    const speed = byStat(BALL.petardo.hitSpeed, a.stats.rebatida);
    let dir = v3(a.heading.x, 0.05, a.heading.z);
    if (aim) {
      const d = sub(aim.position, target.position);
      const l = len(d);
      const t = l / speed;
      const lead = { x: d.x + aim.velocity.x * t, y: d.y + aim.velocity.y * t, z: d.z + aim.velocity.z * t };
      const ll = len(lead);
      dir = v3(lead.x / ll, lead.y / ll, lead.z / ll);
    }
    const dl = len(dir);
    target.velocity = v3((dir.x / dl) * speed, (dir.y / dl) * speed, (dir.z / dl) * speed);
    target.mode = "lancado";
    target.modeTicks = ticks(BALL.petardo.launchedSeconds);
    target.hitBy = a.id;
    plays.push(
      aim ? { kind: "rebatida", athlete: a.id, other: aim.id } : { kind: "rebatida", athlete: a.id },
    );
    return aim;
  };

  for (const a of athletes) {
    if (a.role !== "batedor") continue;
    if (abilities.has(a.id)) {
      // Rebatida Dupla: os dois Petardos ao alcance, cada um num adversário diferente quando dá.
      const near = petardos.filter((p) => dist(p.position, a.position) <= ABILITIES.rebatidaDupla.reach);
      let first: AthleteState | undefined;
      for (const p of near) first = bat(a, p, first?.id) ?? first;
      continue;
    }
    // Rebatida: o batedor que apertou a ação acerta o Petardo mais perto no alcance do taco.
    if (!did(a, BUTTON.action)) continue;
    let target: PetardoState | undefined;
    let td: number = BALL.petardo.batReach;
    for (const p of petardos) {
      const d = dist(p.position, a.position);
      if (d <= td) {
        target = p;
        td = d;
      }
    }
    if (target) bat(a, target);
  }

  for (const p of petardos) {
    if (p.mode === "caca") {
      // Persegue o atleta mais perto (que não esteja atordoado nem sendo ignorado).
      let prey: AthleteState | undefined;
      let pd = Infinity;
      for (const a of athletes) {
        if (a.stunTicks > 0 || a.id === p.ignore) continue;
        const d = dist(a.position, p.position);
        if (d < pd) {
          prey = a;
          pd = d;
        }
      }
      if (prey && pd > 1e-6) {
        const d = sub(prey.position, p.position);
        const want = v3(
          (d.x / pd) * BALL.petardo.chaseSpeed,
          (d.y / pd) * BALL.petardo.chaseSpeed,
          (d.z / pd) * BALL.petardo.chaseSpeed,
        );
        const e = sub(want, p.velocity);
        const el = len(e);
        const cap = BALL.petardo.chaseAcceleration * TICK;
        const k = el > cap ? cap / el : 1;
        p.velocity = { x: p.velocity.x + e.x * k, y: p.velocity.y + e.y * k, z: p.velocity.z + e.z * k };
      }
    } else if (p.mode === "descanso") {
      p.velocity = { x: p.velocity.x * 0.95, y: p.velocity.y * 0.95, z: p.velocity.z * 0.95 };
    }
    p.position = {
      x: p.position.x + p.velocity.x * TICK,
      y: p.position.y + p.velocity.y * TICK,
      z: p.position.z + p.velocity.z * TICK,
    };
    bounceWorld(p.position, p.velocity, BALL.petardo.radius, 0.7, FLIGHT.minHeight - 1);

    // Acertou alguém?
    for (const a of athletes) {
      if (a.stunTicks > 0 || a.id === p.ignore || a.id === p.hitBy) continue;
      if (p.mode === "descanso" || abilityActive(a, "finta")) continue;
      const d = sub(a.position, p.position);
      const dl = len(d);
      if (dl > FLIGHT.athleteRadius + BALL.petardo.radius) continue;
      stun(a);
      plays.push(
        p.hitBy ? { kind: "atordoado", athlete: a.id, other: p.hitBy } : { kind: "atordoado", athlete: a.id },
      );
      // Quica para trás e descansa.
      const n = dl > 1e-6 ? v3(d.x / dl, d.y / dl, d.z / dl) : v3(1, 0, 0);
      p.velocity = v3(-n.x * 8, 3, -n.z * 8);
      p.mode = "descanso";
      p.modeTicks = ticks(BALL.petardo.restSeconds);
      p.ignore = a.id;
      p.ignoreTicks = ticks(BALL.petardo.ignoreVictimSeconds);
      p.hitBy = null;
      break;
    }
    if (p.mode === "caca") p.hitBy = null;
  }

  // ---------------------------------------------------------------- Faísca
  const fp = prev.faisca;
  const fa: FaiscaState = {
    ...fp,
    position: { ...fp.position },
    velocity: { ...fp.velocity },
    waypoint: { ...fp.waypoint },
  };
  if (!fa.active && !fa.captured && tick >= fa.appearTick) {
    fa.active = true;
    plays.push({ kind: "aparece", athlete: "" });
  }
  if (fa.active) {
    fa.activeTicks += 1;
    fa.wanderTicks -= 1;
    if (fa.wanderTicks <= 0 || dist(fa.position, fa.waypoint) < 3) {
      let ux: number;
      let uz: number;
      let uy: number;
      [rng, ux] = signed(rng);
      [rng, uz] = signed(rng);
      [rng, uy] = nextFloat(rng);
      const s = 0.8;
      // Ponto sorteado dentro da elipse do campo.
      const k = ux * ux + uz * uz > 1 ? 1 / Math.sqrt(ux * ux + uz * uz) : 1;
      fa.waypoint = v3(
        ux * k * PITCH.radiusX * s,
        BALL.faisca.minHeight + uy * (BALL.faisca.maxHeight - BALL.faisca.minHeight),
        uz * k * PITCH.radiusZ * s,
      );
      fa.wanderTicks = ticks(BALL.faisca.wanderSeconds);
    }
    let want = sub(fa.waypoint, fa.position);
    // Foge de quem chega perto.
    let near: AthleteState | undefined;
    let nd: number = BALL.faisca.fleeDistance;
    for (const a of athletes) {
      const d = dist(a.position, fa.position);
      if (d < nd) {
        near = a;
        nd = d;
      }
    }
    if (near && nd > 1e-6) {
      const away = sub(fa.position, near.position);
      want = v3(away.x / nd + want.x * 0.02, away.y / nd + 0.2, away.z / nd + want.z * 0.02);
    }
    const minutes = fa.activeTicks / (30 * 60);
    const speed = BALL.faisca.speed * Math.max(0.5, 1 - MATCH.sparkSlowdownPerMinute * minutes);
    const wl = len(want);
    if (wl > 1e-6) {
      const target = v3((want.x / wl) * speed, (want.y / wl) * speed, (want.z / wl) * speed);
      const e = sub(target, fa.velocity);
      const el = len(e);
      const cap = BALL.faisca.acceleration * TICK;
      const k = el > cap ? cap / el : 1;
      fa.velocity = { x: fa.velocity.x + e.x * k, y: fa.velocity.y + e.y * k, z: fa.velocity.z + e.z * k };
    }
    fa.position = {
      x: fa.position.x + fa.velocity.x * TICK,
      y: Math.max(
        BALL.faisca.minHeight,
        Math.min(BALL.faisca.maxHeight, fa.position.y + fa.velocity.y * TICK),
      ),
      z: fa.position.z + fa.velocity.z * TICK,
    };
    bounceWorld(fa.position, fa.velocity, BALL.faisca.radius, 0.5, BALL.faisca.minHeight);

    // Captura: o caçador aperta a ação com a Faísca ao alcance da mão.
    for (const a of athletes) {
      if (a.role !== "cacador" || !did(a, BUTTON.action)) continue;
      const dive = abilityActive(a, "mergulho") ? ABILITIES.mergulho.catchReachFactor : 1;
      if (dist(a.position, fa.position) > FLIGHT.athleteRadius + BALL.faisca.catchReach * dive) continue;
      fa.active = false;
      fa.captured = true;
      fa.capturedBy = a.id;
      fa.velocity = v3();
      plays.push({ kind: "captura", athlete: a.id, team: a.team });
      break;
    }
  }

  // Botões e carga para o próximo tick.
  for (const a of athletes) {
    const now = a.stunTicks > 0 ? 0 : (inputs[a.id]?.buttons ?? 0);
    a.chargeTicks = now & BUTTON.charge ? a.chargeTicks + 1 : 0;
    a.buttons = now;
  }

  return { balls: { pelota: pel, petardos, faisca: fa }, rng, plays };
}
